<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const enabled = ref(false);
const playbook = ref('');
const escalation = ref(15);
const isTogglingCalling = ref(false);
const isSavingPlaybook = ref(false);
const isSavingEscalation = ref(false);

// Input next ignora :min em type="number" e emite '' quando esvaziado; a faixa é
// validada aqui e o botão fica travado, então nunca chega nil na coluna not null.
const escalationValid = computed(
  () =>
    Number.isInteger(escalation.value) &&
    escalation.value >= 5 &&
    escalation.value <= 20
);

watch(
  () => props.inbox,
  inbox => {
    enabled.value = !!inbox.calling_enabled;
    playbook.value = inbox.sales_playbook || '';
    escalation.value = inbox.call_ring_escalation_seconds ?? 15;
  },
  { immediate: true }
);

const updateInbox = async fields => {
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      ...fields,
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    return true;
  } catch (error) {
    useAlert(error?.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
    return false;
  }
};

const handleToggleCalling = async value => {
  enabled.value = value;
  isTogglingCalling.value = true;
  await updateInbox({ calling_enabled: value });
  isTogglingCalling.value = false;
};

const savePlaybook = async () => {
  isSavingPlaybook.value = true;
  await updateInbox({ sales_playbook: playbook.value });
  isSavingPlaybook.value = false;
};

const saveEscalation = async () => {
  isSavingEscalation.value = true;
  await updateInbox({ call_ring_escalation_seconds: escalation.value });
  isSavingEscalation.value = false;
};
</script>

<template>
  <SettingsToggleSection
    :header="t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_TITLE')"
    :description="t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_SUBHEADER')"
    :model-value="enabled"
    @update:model-value="handleToggleCalling"
  >
    <template v-if="enabled" #editor>
      <div class="flex flex-col gap-3 items-start">
        <div class="flex flex-col gap-1">
          <span class="text-heading-4 text-n-slate-12">
            {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PLAYBOOK_TITLE') }}
          </span>
          <span class="text-body-main text-n-slate-11">
            {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PLAYBOOK_SUBHEADER') }}
          </span>
        </div>
        <TextArea
          v-model="playbook"
          :placeholder="
            t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PLAYBOOK_PLACEHOLDER')
          "
          class="w-full"
          auto-height
          min-height="8rem"
          max-height="20rem"
        />
        <NextButton
          :is-loading="isSavingPlaybook"
          :disabled="isTogglingCalling"
          @click="savePlaybook"
        >
          {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PLAYBOOK_SAVE') }}
        </NextButton>

        <div class="flex flex-col gap-1 mt-4">
          <span class="text-heading-4 text-n-slate-12">
            {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RING_ESCALATION_TITLE') }}
          </span>
          <span class="text-body-main text-n-slate-11">
            {{
              t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RING_ESCALATION_SUBHEADER')
            }}
          </span>
        </div>
        <Input
          v-model="escalation"
          type="number"
          class="w-40"
          :label="t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RING_ESCALATION_LABEL')"
          :message="
            escalationValid
              ? ''
              : t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RING_ESCALATION_RANGE_ERROR'
                )
          "
          :message-type="escalationValid ? 'info' : 'error'"
        />
        <NextButton
          :is-loading="isSavingEscalation"
          :disabled="isTogglingCalling || !escalationValid"
          @click="saveEscalation"
        >
          {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RING_ESCALATION_SAVE') }}
        </NextButton>
      </div>
    </template>
  </SettingsToggleSection>
</template>
